<script setup lang="ts">
/**
 * One row of a sidebar navigation: icon, label, count badge. A router link
 * with `to`, otherwise a button that emits `click`.
 *
 * `#action` adds a control at the row's end — a delete or "more" button. A
 * control cannot sit inside the link or button, so the row then becomes a
 * wrapper that holds the item and the action side by side (class, style and
 * `data-*` attributes go to the wrapper). The action shows while the row is
 * hovered or has focus, always on touch; it is the caller's control, with its
 * own label and click, and a click on it never reaches the item.
 */
import { computed, type Component } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';

const props = defineProps<{
  /** RouterLink target. If omitted, renders a plain button (click event). */
  to?: RouteLocationRaw;
  /** Lucide icon component */
  icon?: Component;
  /** Right-aligned count badge */
  count?: number | string | null;
  /**
   * Tone of the count: `danger` when something in the area is overdue,
   * `warning` when something waits for someone (an open pool), `neutral`
   * (default) for a plain tally.
   */
  countTone?: 'neutral' | 'warning' | 'danger';
  /**
   * What the count means, in words — „3 überfällig". Read instead of the bare
   * number and shown as the count's tooltip, because the tone alone is colour.
   */
  countLabel?: string;
  /**
   * Marks the item active by hand: a button, or a link whose area is open on a
   * page the link does not match itself (a record's sub-page).
   */
  active?: boolean;
  /** Disable item (for "coming soon" placeholders) */
  disabled?: boolean;
  /**
   * `compact`: one line without block padding and in full ink — next to a
   * `density="compact"` NxTreeView. Defaults to `default`.
   */
  density?: 'default' | 'compact';
}>();

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void;
}>();

defineSlots<{
  /** The label. */
  default?(): unknown;
  /** A control at the end of the row, e.g. an icon button; shown on hover and focus. */
  action?(): unknown;
}>();

const hasCount = computed(() => props.count != null && props.count !== '' && props.count !== 0);
const countClass = computed(() =>
  props.countTone && props.countTone !== 'neutral' ? `tone-${props.countTone}` : undefined
);
// Joined to the item's name with a comma — a pause before „3 überfällig".
const countSpeech = computed(() => (props.countLabel ? `, ${props.countLabel}` : ''));

const itemClasses = computed(() => ({
  active: props.active,
  disabled: props.disabled,
  'is-compact': props.density === 'compact',
}));
// A link takes its target; a button its type, disabled state and click.
const itemProps = computed(() =>
  props.to
    ? { to: props.to, activeClass: 'active' }
    : {
        type: 'button',
        disabled: props.disabled,
        onClick: (ev: MouseEvent) => emit('click', ev),
      }
);
</script>

<template>
  <div v-if="$slots.action" class="cn-nav-row">
    <component
      :is="to ? RouterLink : 'button'"
      v-bind="itemProps"
      class="cn-nav-item has-action"
      :class="itemClasses"
    >
      <span v-if="icon" class="cn-nav-icon"><component :is="icon" class="nxt-icon-md" /></span>
      <span class="cn-nav-label-text"><slot /></span>
      <span
        v-if="hasCount"
        class="cn-nav-count"
        :class="countClass"
        :title="countLabel"
        :aria-hidden="countLabel ? 'true' : undefined"
      >
        {{ count }}
      </span>
      <span v-if="hasCount && countLabel" class="cn-nav-sr-only">{{ countSpeech }}</span>
    </component>
    <span class="cn-nav-action"><slot name="action" /></span>
  </div>
  <component
    :is="to ? RouterLink : 'button'"
    v-else
    v-bind="itemProps"
    class="cn-nav-item"
    :class="itemClasses"
  >
    <span v-if="icon" class="cn-nav-icon"><component :is="icon" class="nxt-icon-md" /></span>
    <span class="cn-nav-label-text"><slot /></span>
    <span
      v-if="hasCount"
      class="cn-nav-count"
      :class="countClass"
      :title="countLabel"
      :aria-hidden="countLabel ? 'true' : undefined"
    >
      {{ count }}
    </span>
    <span v-if="hasCount && countLabel" class="cn-nav-sr-only">{{ countSpeech }}</span>
  </component>
</template>

<style scoped>
.cn-nav-item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-xs) var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-subtle);
  cursor: pointer;
  transition:
    background var(--nxt-motion-fast),
    color var(--nxt-motion-fast);
  position: relative;
  text-decoration: none;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
}
.cn-nav-item:hover:not(.disabled, .active) {
  background: color-mix(in srgb, var(--nxt-fg) 6%, transparent);
  color: var(--nxt-fg);
}
.cn-nav-item:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
/* Tinted row, full ink and a brand bar inside the row's leading edge — the bar
   sat outside the item before and was clipped by the list. */
.cn-nav-item.active {
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.cn-nav-item.active:hover {
  background: color-mix(in srgb, var(--nxt-brand-bg) 16%, transparent);
}
.cn-nav-item.active::before {
  content: '';
  position: absolute;
  inset-inline-start: 0;
  inset-block: var(--nxt-space-xs);
  width: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
}
/* #action: the item and the control side by side. The control sits over the
   item's end, which keeps room for it, so the row's tint runs behind both. */
.cn-nav-row {
  position: relative;
}
.cn-nav-item.has-action {
  padding-inline-end: calc(var(--nxt-control-size-sm) + var(--nxt-space-xs));
}
.cn-nav-action {
  position: absolute;
  inset-block: 0;
  inset-inline-end: var(--nxt-space-2xs);
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity var(--nxt-motion-fast);
}
.cn-nav-row:hover .cn-nav-action,
.cn-nav-row:focus-within .cn-nav-action {
  opacity: 1;
}
@media (hover: none), (any-pointer: coarse) {
  .cn-nav-action {
    opacity: 1;
  }
}
.cn-nav-item.is-compact {
  min-height: var(--nxt-control-size-sm);
  padding-block: 0;
}
.cn-nav-item.is-compact:not(.active, .disabled) {
  color: var(--nxt-fg);
}
.cn-nav-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cn-nav-icon {
  display: grid;
  place-items: center;
  color: var(--nxt-fg-subtle);
  flex-shrink: 0;
}
.cn-nav-item.active .cn-nav-icon {
  color: var(--nxt-brand-bg);
}

.cn-nav-label-text {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cn-nav-count {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
  background: var(--nxt-brand-subtle-bg);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-md);
  min-width: calc(var(--nxt-size-unit) * 1.125);
  text-align: center;
  flex-shrink: 0;
}
.cn-nav-item.active .cn-nav-count {
  color: var(--nxt-brand-bg);
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
}
/* Solid fills, so an overdue or waiting count stands out of the tally column.
   Same specificity as the active rule above and later in the file: the tone
   outranks the active item's brand tint — an overdue count stays red. */
.cn-nav-item .cn-nav-count.tone-warning {
  color: var(--nxt-warning-fg);
  background: var(--nxt-warning-bg);
}
.cn-nav-item .cn-nav-count.tone-danger {
  color: var(--nxt-danger-fg);
  background: var(--nxt-danger-bg);
}

.cn-nav-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(
    var(--nxt-size-unit) * -0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .cn-nav-item,
  .cn-nav-action {
    transition: none;
  }
}
</style>
